import { createAnthropic } from '@ai-sdk/anthropic'
import { createGoogleGenerativeAI } from '@ai-sdk/google'
import { createOpenAI } from '@ai-sdk/openai'
import { customProvider } from 'ai'
import { z } from 'zod'

/** AI providers and their required env vars */

export const aiProviderSchema = z.enum(['openai', 'anthropic', 'gemini'])

export interface AIProviderSpec {
  baseURL: string

  authHeader: {
    name: string
    bearer: boolean
  }

  getTokensUsed: (response: unknown) => number

  // Path to the secret containing the API key in Parameter Store
  apiKeySecret: string
}

export type AIProvider = z.infer<typeof aiProviderSchema>

const providerSpecs = {
  openai: {
    baseURL: 'https://api.openai.com/v1',
    authHeader: {
      name: 'Authorization',
      bearer: true,
    },
    apiKeySecret: '/tunasong/openai/api-key',
    getTokensUsed: (response: unknown) => {
      const schema = z.object({
        usage: z.object({
          total_tokens: z.number(),
        }),
      })
      const parsedResponse = schema.parse(response)
      return parsedResponse.usage.total_tokens
    },
  },
  anthropic: {
    baseURL: 'https://api.anthropic.com/v1',
    authHeader: {
      name: 'x-api-key',
      bearer: false,
    },
    apiKeySecret: '/tunasong/anthropic/api-key',
    getTokensUsed: (response: unknown) => {
      const schema = z.object({
        usage: z.object({
          input_tokens: z.number(),
          output_tokens: z.number(),
        }),
      })
      const parsedResponse = schema.parse(response)
      return parsedResponse.usage.input_tokens + parsedResponse.usage.output_tokens
    },
  },
  gemini: {
    baseURL: 'https://generativelanguage.googleapis.com/v1beta',
    authHeader: {
      name: 'x-goog-api-key',
      bearer: false,
    },
    apiKeySecret: '/tunasong/google/gemini-api-key',
    getTokensUsed: (response: unknown) => {
      const schema = z.object({
        usageMetadata: z.object({
          totalTokenCount: z.number(),
        }),
      })
      const parsedResponse = schema.parse(response)
      return parsedResponse.usageMetadata.totalTokenCount
    },
  },
} satisfies Record<AIProvider, AIProviderSpec & Record<string, unknown>>

export const getProviderByModel = (model: SupportedModel) => {
  for (const provider in supportedModelsByProvider) {
    if (supportedModelsByProvider[provider].includes(model)) {
      return provider as AIProvider
    }
  }
  return null
}

/**
 * Sampling parameters the Anthropic Messages API no longer accepts.
 *
 * These are the Vercel AI SDK's camelCase names; the Anthropic adapter maps
 * them to `temperature` / `top_p` / `top_k` on the wire.
 */
const anthropicUnsupportedSamplingParams = ['temperature', 'topP', 'topK'] as const

/**
 * Drop sampling parameters that the resolved provider rejects.
 *
 * `temperature`, `top_p` and `top_k` are **removed** on current Anthropic
 * models (Claude Opus 4.7 and later, Claude Sonnet 5) — sending any of them
 * returns a 400. Our request schemas carry `.default()`s
 * (`chat.schema.ts`, `text-params.ts`), so `temperature` is present on every
 * outbound request whether or not a caller asked for one.
 *
 * Applied here, at the single outbound chokepoint every text call passes
 * through, rather than at each of the five call sites. The fields stay on the
 * public request schemas: OpenAI and Gemini still accept them, and only the
 * Anthropic request omits them.
 *
 * Provider-conditional, not model-conditional, on purpose: it must stay
 * correct across Anthropic id bumps without a second table to keep in sync,
 * and Anthropic's own guidance is to steer behaviour by prompting instead.
 */
export const stripUnsupportedSamplingParams = <T extends object>(model: SupportedModel, params: T): T => {
  if (getProviderByModel(model) !== 'anthropic') {
    return params
  }
  const dropped: readonly string[] = anthropicUnsupportedSamplingParams
  return Object.fromEntries(Object.entries(params).filter(([key]) => !dropped.includes(key))) as T
}

export const getMinuteCostByTranscriptionModel = (model: SupportedTranscribeModel) => transcribeModelCost[model]

export const getTokenCostByModel = (model: SupportedModel) => aiModelTokenCost[model]

export const getProviderSpec = (provider: AIProvider, type: 'client' | 'server' = 'server') => {
  const spec = providerSpecs[provider]
  if (type === 'client') {
    return {
      ...spec,
      baseURL: `https://tunasong.com/stream/proxy`,
    }
  }
  return spec
}

export const getProviderApiKeySecret = (provider: AIProvider) => providerSpecs[provider]?.apiKeySecret

const createProxyFetch = ({ model, authToken }: { model: SupportedModel; authToken: string }) => {
  /** Proxy fetch to our API   */
  const proxyFetch: typeof fetch = async (input: string | Request | URL, options: RequestInit = {}) => {
    // Send the body, headers in the body. We do this to preserve the headers
    // and avoid CORS issues
    const url = typeof input === 'string' ? input : input.toString()

    const { headers, body } = input instanceof Request ? input : options

    // Remove the authorization header from the request

    return fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        model,
        provider: getProviderByModel(model),
        payload: body,
        headers,
      }),
    })
  }
  return proxyFetch
}

/** Custom provider. authToken is JWT token in client mode, and API key in server mode */
const getAIModelInternal = ({
  type,
  authToken,
  model,
}: {
  type: 'client' | 'server'
  authToken: string
  model: SupportedModel
}) => {
  const proxyFetch = createProxyFetch({ model, authToken })
  const baseURL = type === 'client' ? 'https://tunasong.com/stream/proxy' : undefined
  const apiKey = type === 'server' ? authToken : 'NOT_USED'

  /** Create custom providers with proxyFetch */
  const openai = createOpenAI({
    baseURL,
    apiKey,
    fetch: type === 'client' ? proxyFetch : fetch,
  })
  const anthropic = createAnthropic({
    baseURL,
    fetch: type === 'client' ? proxyFetch : fetch,
    apiKey,
  })
  const gemini = createGoogleGenerativeAI({
    baseURL,
    fetch: type === 'client' ? proxyFetch : fetch,
    apiKey,
  })

  const transcribeModels = {
    'whisper-1': openai.transcription('whisper-1'),
    'gpt-4o-transcribe': openai.transcription('gpt-4o-transcribe'),
    'gpt-4o-mini-transcribe': openai.transcription('gpt-4o-mini-transcribe'),
  }

  /**
   * ADD YOUR MODELS HERE
   */
  const languageModels = {
    // OpenAI models
    'gpt-5.5': openai('gpt-5.5'),
    'gpt-5.4': openai('gpt-5.4'),
    'gpt-5.4-mini': openai('gpt-5.4-mini'),
    'gpt-4o-audio-preview': openai('gpt-4o-audio-preview'),
    // Anthropic models
    // https://platform.claude.com/docs/en/about-claude/models/overview
    'claude-opus-4-7': anthropic('claude-opus-4-7'),
    'claude-opus-4-6': anthropic('claude-opus-4-6'),
    'claude-sonnet-4-6': anthropic('claude-sonnet-4-6'),
    'claude-sonnet-4-5': anthropic('claude-sonnet-4-5'),
    // Google Gemini models
    'gemini-2.5-flash': gemini('gemini-2.5-flash'),
    'gemini-2.5-pro': gemini('gemini-2.5-pro'),
  }

  const provider = customProvider({
    languageModels,

    fallbackProvider: openai,
  })
  return { provider, openai, anthropic, gemini, languageModels, transcribeModels }
}

export const getAIModel = ({
  type,
  authToken,
  model,
}: {
  type: 'client' | 'server'
  authToken: string
  model: SupportedModel
}) => {
  const { provider } = getAIModelInternal({ type, authToken, model })
  return provider.languageModel(model)
}

export const getTranscriptionModel = ({
  type,
  authToken,
  model,
}: {
  type: 'client' | 'server'
  authToken: string
  model: SupportedTranscribeModel
}) => {
  const { openai } = getAIModelInternal({ type, authToken, model })

  const transcriptionModel = openai.transcription(model)
  return transcriptionModel
}

// The cost is per minute of transcription
export const transcribeModelCost = {
  'whisper-1': 100,
  // @todo gpt-4o requires mp3 or wav
  // 'gpt-4o-transcribe': 100,
}

export type SupportedTranscribeModel = keyof typeof transcribeModelCost

/**
 * This also defines the models.
 *
 * These are *relative* quota weights consumed by `updateTokenQuota`, not
 * prices. They are not currently derived from a single formula — `gpt-5.4`
 * sits at 12 against `claude-opus-4-6`'s 3 despite a lower published rate — so
 * a coherent re-derivation of the whole table is still owed. Until then, a new
 * id takes the weight of the closest sibling whose published rate can be
 * checked, and existing weights are left alone so quota behaviour does not
 * move under users as a side effect of registering a model.
 */
export const aiModelTokenCost = {
  'whisper-1': 0.006,
  // 2x gpt-5.4 on both input ($2.50 -> $5.00) and output ($15 -> $30) per
  // OpenAI's published pricing, so 2x the gpt-5.4 weight.
  'gpt-5.5': 24,
  'gpt-5.4': 12,
  'gpt-5.4-mini': 2,
  // Rename of the never-valid `gpt-4-audio-preview` slot. OpenAI does not
  // publish a rate for the audio preview on its pricing page, so the slot's
  // existing weight is carried rather than invented.
  'gpt-4o-audio-preview': 5,
  // Opus 4.7 and 4.6 are both $5/$25 per MTok; Sonnet 4.6 and 4.5 are both
  // $3/$15. Equal published rates, so equal weights.
  'claude-opus-4-7': 3,
  'claude-opus-4-6': 3,
  'claude-sonnet-4-6': 2,
  'claude-sonnet-4-5': 2,
  'gemini-2.5-flash': 1,
  // Rename of the never-valid `gemini-3.0-pro-preview` slot, carrying its
  // weight. Gemini 2.5 Pro is $1.25/$10.00 — below Sonnet's $3/$15 at weight
  // 2 — so 3 over-charges slightly rather than under-charging.
  'gemini-2.5-pro': 3,
  // Music-generation quota is billed per second of audio (see music-task.ts),
  // not per token. v2 is the current default (introducing-music-v2).
  'elevenlabs-music-v1': 100,
  'elevenlabs-music-v2': 100,
}

export type SupportedModel = keyof typeof aiModelTokenCost

/**
 * ElevenLabs music-generation model ids (as sent to the ElevenLabs API), kept
 * here in the browser-safe registry so both the Lambda worker and the client
 * RTK query can share the union without pulling in the node-only SDK wrapper.
 * v2 adds better vocals/arrangement, multilingual, and section-by-section
 * composition — see https://elevenlabs.io/blog/introducing-music-v2.
 */
export const elevenLabsMusicModels = ['music_v1', 'music_v2'] as const
export type ElevenLabsMusicModel = (typeof elevenLabsMusicModels)[number]

export const defaultElevenLabsMusicModel: ElevenLabsMusicModel = 'music_v2'

/** Map an ElevenLabs music model id to its internal quota model key. */
export const musicQuotaModel = (modelId: ElevenLabsMusicModel): SupportedModel =>
  modelId === 'music_v2' ? 'elevenlabs-music-v2' : 'elevenlabs-music-v1'

const supportedModelsByProvider: Record<string, [SupportedModel, ...SupportedModel[]]> = {
  openai: [
    'gpt-5.5',
    'gpt-5.4',
    'gpt-5.4-mini',
    'gpt-4o-audio-preview',
    // 'whisper-1',
  ],
  anthropic: ['claude-opus-4-7', 'claude-opus-4-6', 'claude-sonnet-4-6', 'claude-sonnet-4-5'],
  gemini: ['gemini-2.5-flash', 'gemini-2.5-pro'],
}

const aiModelNames: SupportedModel[] = Object.keys(aiModelTokenCost) as SupportedModel[]

/** Chat/language models selectable in the UI (excludes transcription and music-only models) */
const selectableModels = aiModelNames.filter(
  m => !['whisper-1', 'elevenlabs-music-v1', 'elevenlabs-music-v2'].includes(m)
) as [SupportedModel, ...SupportedModel[]]

export const aiModelsSchema = z.enum(selectableModels)
